// Angular
import { Injectable, OnDestroy } from '@angular/core';

// Ionic
import { Geocoder, GeocoderResult } from '@ionic-native/google-maps/ngx';
import {NativeGeocoder, NativeGeocoderOptions} from '@ionic-native/native-geocoder';
import {NativeGeocoderResult} from '@ionic-native/native-geocoder/ngx';

// Reactive X
import { defer, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

// Internal dependencies
import { AnalyticsService } from './analytics.service';
import { Platform } from '@ionic/angular';

@Injectable({
  providedIn: 'root',
})
export class GeocodingService implements OnDestroy {
  /* ATTRIBUTES */

  private imminentDestruction$: Subject<void> = new Subject();

  /* LIFECYCLE */

  public constructor(
    private analytics: AnalyticsService,
    private platform: Platform,
    ) {}

  public ngOnDestroy(): void {
    this.imminentDestruction$.next();
    this.imminentDestruction$.complete();
  }

  /* METHODS */

  public geocode(address: string): Observable<GeocoderResult> {
    return defer(() => {
      return Geocoder.geocode({ address });
    }).pipe(
      map((geocodingResults: GeocoderResult[]) => {
        const result = geocodingResults[0];

        if (!result) {
          this.analytics.captureEvent({
            message: 'Geocoding did not return a result for address',
            level: AnalyticsService.LEVEL_DEBUG,
            extra: {
              address,
              result,
            },
          });

          throw new Error('Geocoding failed');
        }

        return result;
      }),
    );
  }

  public reverseGeocode(latitude: number, longitude: number): Observable<NativeGeocoderResult> {
    // Limit coordinates to six decimal places
    const lat: number = Math.round(latitude * 1000000) / 1000000;
    const lng: number = Math.round(longitude * 1000000) / 1000000;

    console.log('reverseGeocode', lat, lng);
    return defer(() => {
      let options: NativeGeocoderOptions = {
        useLocale: true,
        maxResults: 2
      };
      return NativeGeocoder.reverseGeocode(lat, lng, options);
    }).pipe(
      map((geocodingResults: any) => {
        const result = geocodingResults[0];

        if (!result) {
          this.analytics.captureEvent({
            message: 'Reverse geocoding did not return a result for coordinates',
            level: AnalyticsService.LEVEL_DEBUG,
            extra: {
              latitude,
              latitudeFixed: lat,
              longitude,
              longitudeFixed: lng,
              result,
            },
          });

          throw new Error('Reverse geocoding failed');
        }

        return result;
      }),
    );
  }

  /* UTILITY */

  public parseAddress(geocoderResult?: NativeGeocoderResult): string {
    console.log('parseAddress Geocoding', JSON.stringify(geocoderResult));
    if (!geocoderResult) return '';

    let address: string = '';

    // Street
    if (geocoderResult.thoroughfare && geocoderResult.thoroughfare !== 'Unnamed Road') {
      address += geocoderResult.thoroughfare;
    }

    // Street number
    if (geocoderResult.subThoroughfare) {
      address += address.length > 0 ? ` ${geocoderResult.subThoroughfare}` : geocoderResult.subThoroughfare;
    }

    // Postal code
    if (geocoderResult.postalCode) {
      address += address.length > 0 ? ', ' : '';
      address += geocoderResult.postalCode;
    }

    // City
    if (geocoderResult.locality) {
      address += address.length > 0 ? ` ${geocoderResult.locality}` : geocoderResult.locality;
    }

    // Country
    if (geocoderResult.countryName) {
      address += address.length > 0 ? ', ' : '';
      address += geocoderResult.countryName;
    }

    return address;
  }
}
